import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, OnDestroy, computed, effect, input, model, signal, untracked, viewChild } from '@angular/core';
import { Dialog } from 'primeng/dialog';
import { Button } from 'primeng/button';
import {
	ImageGenerateTimeoutError,
	dataUrlToBlob,
	generateImagePreview,
	imageDownloadName,
	imageGenerateBlocker,
	imageGenerateTimeoutMessage,
	previewAspectRatio,
	type ImageGenerateTarget,
} from '../../../../shared/utils/image-generate';
import type { ImageRefDimensions } from '../../../../shared/utils/image-ref-dimensions';
import { removePlayerElement } from '../../utils/player-teardown';

/** Everything one render needs, snapshotted by the editor at the moment of the click. */
export interface ImagePreviewRequest {
	projectSlug: string;
	signatureUuid: string;
	imageSlug: string;
	/** Human name for the header; falls back to the slug. */
	imageName: string;
	/** The image's entry in project.json — the only source of its render size. */
	ref: ImageRefDimensions | null;
	/** Editor path of the image; decides whether the player can reach it at all. */
	imagePath: string;
	args: Record<string, string | number | boolean>;
	vars: Record<string, string | number | boolean>;
	/** Backend serving this project's working copy. */
	jsonApiUrl: string;
	region: string;
}

/** The slice of `<webout-player>` an image render uses. */
interface WeboutImagePlayerElement extends HTMLElement {
	projectSlug: string;
	signatureUuid: string;
	args: Record<string, string | number | boolean>;
	vars: Record<string, string | number | boolean>;
	disableAnalytics: boolean;
	generateImage(imageSlug: string, playIcon?: boolean): Promise<string>;
}

/**
 * Render one image and show it at its own aspect ratio (DEV-655).
 *
 * Why a second player rather than the centre one: the centre
 * `<webout-player>` is mounted on *chapters* and re-mounted by the editor's
 * save and config effects. `generateImage()` needs neither `init()` nor a
 * chapter, and entangling it with the playback lifecycle would buy nothing —
 * so this owns a short-lived element of its own, created on open and gone
 * again before the dialog closes. The rules it follows (deadline, teardown on
 * every path, which images are renderable at all) are in `image-generate.ts`;
 * the teardown itself is the one `player-panel` uses.
 */
@Component({
	selector: 'app-image-preview-dialog',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [Dialog, Button],
	schemas: [CUSTOM_ELEMENTS_SCHEMA],
	templateUrl: './image-preview-dialog.html',
	styleUrl: './image-preview-dialog.scss',
})
export class ImagePreviewDialogComponent implements OnDestroy {
	visible = model<boolean>(false);
	/** A fresh object per click; identity is what triggers a render. */
	request = input<ImagePreviewRequest | null>(null);

	protected status = signal<'idle' | 'rendering' | 'done' | 'error'>('idle');
	protected dataUrl = signal<string | null>(null);
	protected errorMessage = signal<string | null>(null);

	protected header = computed(() => {
		const req = this.request();
		if (!req) return 'Image preview';
		return `Image preview — ${req.imageName || req.imageSlug}`;
	});
	protected frameAspect = computed(() => previewAspectRatio(this.request()?.ref) ?? '16 / 9');
	protected sizeLabel = computed(() => {
		const size = this.request()?.ref?.dimensions;
		return size?.width && size?.height ? `${size.width} × ${size.height}` : null;
	});

	/**
	 * Hosts the detached player. Kept out of the flow and out of the accessibility
	 * tree: the element paints into its own canvas, which `generateImage()` reads
	 * back as a data URL, so it never needs to be seen. It still has to be
	 * *connected* — Lit only reaches `firstUpdated` (and so only fires
	 * `playerReady`) once the element is in the document.
	 */
	private stage = viewChild<ElementRef<HTMLDivElement>>('stage');

	private playerEl: WeboutImagePlayerElement | null = null;
	/** Invalidates an in-flight render whose result nobody wants any more. */
	private runToken = 0;
	private ranFor: ImagePreviewRequest | null = null;
	private objectUrl: string | null = null;

	constructor() {
		effect(() => {
			const req = this.request();
			const open = this.visible();
			untracked(() => {
				if (!open || !req) {
					this.reset();
					return;
				}
				if (req === this.ranFor) return;
				this.ranFor = req;
				void this.render(req);
			});
		});
	}

	ngOnDestroy(): void {
		this.reset();
	}

	protected close(): void {
		this.visible.set(false);
	}

	/** Re-run the same request — useful after fixing media or a width. */
	protected rerender(): void {
		const req = this.request();
		if (req) void this.render(req);
	}

	protected download(): void {
		const req = this.request();
		const url = this.dataUrl();
		if (!req || !url) return;

		// Revoke the previous one first: a viewer who downloads twice would
		// otherwise leak a blob per click for the lifetime of the document.
		this.revokeObjectUrl();
		this.objectUrl = URL.createObjectURL(dataUrlToBlob(url));

		const link = document.createElement('a');
		link.href = this.objectUrl;
		link.download = imageDownloadName(req.projectSlug, req.imageSlug);
		document.body.appendChild(link);
		link.click();
		link.remove();
	}

	private async render(req: ImagePreviewRequest): Promise<void> {
		const token = ++this.runToken;
		this.detachPreviewPlayer();
		this.dataUrl.set(null);
		this.errorMessage.set(null);

		const blocker = imageGenerateBlocker(req.imagePath, req.ref);
		if (blocker) {
			this.status.set('error');
			this.errorMessage.set(blocker);
			return;
		}

		const host = this.stage()?.nativeElement;
		if (!host) return;

		this.status.set('rendering');

		// These are globals the player reads lazily at fetch time, and it shares
		// them with the centre preview. The editor points both at the same
		// backend and region, so writing them is a no-op in practice — done
		// anyway so a render is still correct if it happens before the centre
		// player has ever mounted. (`volcanoUrl` mirrors `player-panel`: it
		// resolves this project's own relative-path resources, not the player's
		// shared assets, which ride a separate getter.)
		const win = window as unknown as Record<string, string | undefined>;
		win['weboutRegion'] = req.region;
		win['jsonApiUrl'] = req.jsonApiUrl;
		win['volcanoUrl'] = req.jsonApiUrl;

		const player = document.createElement('webout-player') as WeboutImagePlayerElement;
		player.projectSlug = req.projectSlug;
		player.signatureUuid = req.signatureUuid;
		player.args = req.args;
		player.vars = req.vars;
		player.disableAnalytics = true;
		silenceImageAnalytics(player);

		// Subscribed before the element is connected: Lit fires `playerReady`
		// from `firstUpdated`, on a microtask after `connectedCallback`, and a
		// listener attached after `appendChild` can lose that race — after which
		// nothing ever fires again and the render only ends on the deadline.
		const ready = new Promise<void>(resolve => {
			player.addEventListener('playerReady', () => resolve(), { once: true });
		});

		this.playerEl = player;
		host.appendChild(player);

		const target: ImageGenerateTarget = {
			whenReady: () => ready,
			generate: () => player.generateImage(req.imageSlug, false),
			teardown: () => this.detachPreviewPlayer(),
		};

		try {
			const url = await generateImagePreview(target);
			if (token !== this.runToken) return;
			this.dataUrl.set(url);
			this.status.set('done');
		} catch (err) {
			if (token !== this.runToken) return;
			this.status.set('error');
			this.errorMessage.set(
				err instanceof ImageGenerateTimeoutError
					? imageGenerateTimeoutMessage(req.ref)
					: err instanceof Error ? err.message : String(err),
			);
		}
	}

	/**
	 * Idempotent by design: `generateImagePreview()`'s `finally` calls this, and
	 * so does closing the dialog — which routinely happens first, while a render
	 * is still in flight.
	 */
	private detachPreviewPlayer(): void {
		const player = this.playerEl;
		if (!player) return;
		this.playerEl = null;
		removePlayerElement(player, 'image-preview-dialog', { slug: this.request()?.projectSlug });
	}

	private reset(): void {
		this.runToken++;
		this.ranFor = null;
		this.detachPreviewPlayer();
		this.revokeObjectUrl();
		this.status.set('idle');
		this.dataUrl.set(null);
		this.errorMessage.set(null);
	}

	private revokeObjectUrl(): void {
		if (!this.objectUrl) return;
		URL.revokeObjectURL(this.objectUrl);
		this.objectUrl = null;
	}
}

/**
 * Keep a Studio image render out of the client's campaign statistics.
 *
 * DEV-675 made every editor preview pass `disableAnalytics`, but the player
 * honours that flag only on the `init()` path: `generateImage()` goes through
 * `app.initImage()`, which never calls `analyticsService.init()`, so the flag
 * stays at its `false` default and the render POSTs `init`, `imageGenerated`
 * and `destroy` hits to the stats endpoint. Verified against the pinned
 * 1.6.214 bundle, not just the source checkout.
 *
 * Setting the service's own flag is the only lever from out here, and it is
 * best-effort on purpose: if a later player version renames this or fixes the
 * gap properly, the guard quietly does nothing and we are no worse off than
 * today. Worth raising with the player side alongside the never-settling
 * `generateImage()` promise — same package, same conversation.
 */
function silenceImageAnalytics(player: unknown): void {
	try {
		const analytics = (player as { app?: { analyticsService?: { init?(disable: boolean): void } } })
			.app?.analyticsService;
		analytics?.init?.(true);
	} catch {
		// Internals moved; the render is still the point.
	}
}
